import { authorsfetchCard, authorsGetCard } from "./authors.js";
import CONFIG from "./../config.json";
import { cacheGetCacheTime } from "./cache.js";
import {browserImageIsReady,
        browserCanShare,
        browserShare} from "./browser.js";
import {postersSetPosterResposiveWidth,
        getPostersMaximumImageWidth,
        postersApplyPosterImageAspectRatio,
        postersReplaceApostrophesInText,
        postersEscapeSpacesAndApostrophes,
        postersGetYear,
        postersGetDateString,
        postersGetEndDateOrTime} from "./posters.js";
import {mapsRegion,
        mapsTown,
        mapsLabel} from "./maps.js";

import {validateAndReturnComments }from "./validate.js";
import { labelsFetchCatalog, labelsGetDisplayLabel, labelsHydratePosterLabels } from "./labels.js";
import { i18nPosterImageAlt, i18nText } from "./i18n.js";


const POSTER_MINIMUM_WIDTH = 290;
const POSTER_CONTACT_ENDPOINT = "/api/poster/contact";
const POSTER_CONTACT_MESSAGE_MAX_LENGTH = 1000;
const POSTER_CONTACT_RESEND_DELAY_MS = 5 * 60 * 1000;
const POSTER_CONTACT_SENT_PREFIX = "poster-contact-sent:";
const _pendingPosterContacts = new Set();

const POSTER_CONTACT_REASONS = {
    owner_modify: "reasonOwnerModify",
    owner_remove: "reasonOwnerRemove",
    other: "reasonOther"
};

function posterGetContentLanguage(poster) {
    return poster.contentLanguage === "es" ? "es" : "ca";
}

function posterLocalizePath(path, language) {
    let normalizedPath = path;
    if (normalizedPath.startsWith("/es/")) {
        normalizedPath = normalizedPath.substring(3);
    } else if (normalizedPath === "/es") {
        normalizedPath = "/";
    } else if (normalizedPath.startsWith("/ca/")) {
        normalizedPath = normalizedPath.substring(3);
    } else if (normalizedPath === "/ca") {
        normalizedPath = "/";
    }
    if (language === "es") {
        if (normalizedPath === "/") {
            return "/es/";
        }
        return "/es" + normalizedPath;
    }
    if (normalizedPath === "/") {
        return "/ca/";
    }
    return "/ca" + normalizedPath;
}

function posterGetRelativeLink(poster) {
    const language = posterGetContentLanguage(poster);
    return posterLocalizePath(
            "/" +
            encodeURIComponent(poster.id) +
            ".html?id=" +
            poster.publisherId,
            language
            );
}

function posterFetchPosterId(parent, id, wrapper) {
    return new Promise(function (resolve, reject) {
        let resource =
                `${CONFIG.posters_api_url}/resource/poster/public/${parent}/${id}`;

        const cacheTime = cacheGetCacheTime();
        if (cacheTime !== null && cacheTime !== "") {
            resource += "?cacheTime=" + cacheTime;
        }

        fetch(resource)
                .then((response) => response.json())
                .then((aux) => {
                    posterCreateAndAppend(aux, wrapper);
                    resolve(true);
                })
                .catch((err) => {
                    reject(err);
                });
    });
}

function posterFetchPosterData(parent, id) {
    return new Promise(function (resolve, reject) {
        let resource =
                `${CONFIG.posters_api_url}/resource/poster/public/${parent}/${id}`;

        const cacheTime = cacheGetCacheTime();
        if (cacheTime !== null && cacheTime !== "") {
            resource += "?cacheTime=" + cacheTime;
        }

        fetch(resource)
                .then((response) => response.json())
                .then((aux) => {
                    resolve(aux);
                })
                .catch((err) => {
                    reject(err);
                });
    });
}

function posterStartUp() {
    const params = new URL(document.location).searchParams;
    const posterId = params.get("id");
    const posterParent = params.get("parent");

    posterFetchPosterId(
            posterParent,
            posterId,
            document.getElementById("mainPostersWrapper")
            )
            .then(() => {
            })
            .catch((err) => {
                window.alert("Alguna cosa ha anat malament: " + err);
                console.log(err.stack);   
            })
            .finally(() => {
                document.getElementById("mainLoader").style = "display: none;";
            });
}

function posterRender(parent = document) {
    const wrappers = parent.getElementsByClassName("posterWrapper");

    if (wrappers.length > 0) {
        [].forEach.call(wrappers, (poster) => {
            posterApplyResponsiveImageSizing(poster);
        });
}
}

function posterApplyResponsiveImageSizing(poster) {
    const posterImages = poster.getElementsByClassName("posterImage");

    if (posterImages.length === 0) {
        return;
    }

    browserImageIsReady(posterImages[0]).then(() => {
        postersApplyPosterImageAspectRatio(
                poster,
                posterImages[0].naturalWidth,
                posterImages[0].naturalHeight
                );
        const maximumImageWidth = getPostersMaximumImageWidth(
                posterImages[0].naturalWidth,
                posterImages[0].naturalHeight
                );
        postersSetPosterResposiveWidth(poster, maximumImageWidth);
    });
}

function posterCreatePosterElement(poster) {
    // Create poster element ---------------------------------------------------
    const wrapper = document.createElement("article");
    const posterLanguage = posterGetContentLanguage(poster);
    const text = i18nText(posterLanguage);
    wrapper.setAttribute("class", "posterWrapper mainItem");
    wrapper.setAttribute("data-author-id", poster.publisherId);
    wrapper.setAttribute("data-post-id", poster.id);
    wrapper.setAttribute("data-post-lng", poster.geoPt.longitude);
    wrapper.setAttribute("data-post-lat", poster.geoPt.latitude);
    wrapper.setAttribute("lang", posterGetContentLanguage(poster));
    wrapper._posterData = poster;
    //
    const relativeLink = posterGetRelativeLink(poster);
    let posterTemplate =
            "<h3><a class='posterTitle' href='" +
            relativeLink +
            "'>" +
            poster.title +
            "</a></h3>" +
            "<h4>" +
            poster.subtitle +
            "</h4>";
    posterTemplate += "<div><a href='" + relativeLink;
    posterTemplate +=
            "'><div class='posterImageWrapper'><img class='posterImage' src='" +
            poster.pictureUrl +
            "' alt='" +
            postersReplaceApostrophesInText(
                    i18nPosterImageAlt(
                            poster.title,
                            poster.town,
                            postersGetYear(poster.dateBegin),
                            posterLanguage
                            )
                    ) +
            "'></div></a></div>";
    posterTemplate += "<div class='posterDateTimeWrapper'><div>";
    posterTemplate += postersGetDateString(poster.dateBegin, posterLanguage);
    posterTemplate += "</div><div>";
    posterTemplate += postersGetEndDateOrTime(
            poster.dateBegin,
            poster.dateEnd,
            poster.timeBegin,
            posterLanguage
            );
    posterTemplate += "</div></div>";
    posterTemplate +=
            "<div class='posterMoreInfo'><div class='posterWebLinkWrapper'>";
    posterTemplate +=
            "<a class='posterWebLink' href='http://" +
            poster.webLink +
            "'>" +
            poster.webLink +
            "</a>";
    posterTemplate += "</div>";
    posterTemplate += "<a class='posterTooltip' href='" + relativeLink + "'>?</a></div>";
    posterTemplate += "<div class='posterCommentsWrapper'>";
    posterTemplate += validateAndReturnComments(poster.comments);
    posterTemplate += "</div>";
    posterTemplate += "<div class='posterTags'>";
    posterTemplate += "<div class='posterRegionWrapper'>";
    const posterIdParam = encodeURIComponent(poster.id);
    const posterParentParam = encodeURIComponent(poster.publisherId);
    posterTemplate +=
            "<a class='posterLocation' href='" + posterLocalizePath("/map.html", posterLanguage) + "?lat=" +
            poster.geoPt.latitude +
            "&lng=" +
            poster.geoPt.longitude +
            "&zoom=15&posterId=" +
            posterIdParam +
            "&posterParent=" +
            posterParentParam +
            "'><img src='/images/map_ccc.png' class='posterLocationImage'></a>";
    posterTemplate +=
            "<a class='posterLabel' href='" +
            posterLocalizePath(mapsTown(poster.region, poster.town), posterLanguage) +
            "'>" +
            poster.town +
            "</a>";
    posterTemplate +=
            "<a class='posterLabel' href='" +
            posterLocalizePath(mapsRegion(poster.region), posterLanguage) +
            "'>" +
            poster.region +
            "</a>";
    posterTemplate += "</div>";
    posterTemplate += "<div class='posterLabelWrapper'>";
    poster.labelsList.forEach((value) => {
        posterTemplate +=
                "<a class='posterLabel' href='" +
                posterLocalizePath(mapsLabel(poster.region, value), posterLanguage) +
                "' data-label-value='" +
                postersReplaceApostrophesInText(value) +
                "' data-label-language='" +
                posterLanguage +
                "'>" +
                labelsGetDisplayLabel(value, posterLanguage) +
                "</a>";
    });
    posterTemplate += "</div>";
    posterTemplate += "</div>";
    posterTemplate += "<div class='posterAuthor' style='clear:both;'><div class='poster-pre'>";
    posterTemplate += text.publishedBy + ":</div>";
    posterTemplate +=
            "<div class='posterAuthorWrapper' data-author-attached=false data-author-id='";
    posterTemplate += poster.publisherId;
    posterTemplate += "'></div></div>";

    posterTemplate +=
            "<div class='posterSocial' style='clear:both; padding-top: 1em; padding-bottom: 4px;'><div class='poster-pre'>" +
            text.posterActions +
            "</div>" +
            "<div class='posterShare' data-href='https://cartells.yuppiya.com" +
            relativeLink +
            "' data-poster-id='" +
            postersReplaceApostrophesInText(poster.id) +
            "' data-publisher-id='" +
            postersReplaceApostrophesInText(poster.publisherId) +
            "' data-poster-title='" +
            postersReplaceApostrophesInText(poster.title) +
            "'></div>" +
            // + "<div style='margin-top: 1em;'><div class='fb-like' data-width='' data-layout='button_count' data-action='like' data-size='large' data-share='true' data-href='https://cartells.yuppiya.com/"
            // + relativeLink
            // + "'></div></div>"
            "</div>";

    //
    wrapper.innerHTML = posterTemplate;
    labelsHydratePosterLabels(wrapper);

    return wrapper;
}

function posterParseDocument() {
    const posterSocial = document.getElementById("posterSocial");
    posterParse(posterSocial);
}

function posterParse(element) {
    if (!element) {
        return;
    }
    const text = i18nText(element.closest("[lang]")?.getAttribute("lang") || document.documentElement.lang);

    const share = element.getElementsByClassName("posterShare");
    if (share.length === 0) {
        return;
    }

    if (browserCanShare() === true) {
        const anchor = document.createElement("a");
        anchor.setAttribute("class", "anchorShare");
        anchor.href = "javascript:void(0)";
        anchor.innerText = text.share;
        share[0].appendChild(anchor);

        anchor.addEventListener("click", (event) => {
            browserShare(share[0].dataset.href);
        });
    }

    posterAppendContactButton(share[0]);
}

function posterAppendContactButton(shareElement) {
    if (shareElement.getAttribute("data-contact-attached") === "true") {
        return;
    }
    const text = i18nText(shareElement.closest("[lang]")?.getAttribute("lang") || document.documentElement.lang);

    const anchor = document.createElement("a");
    anchor.setAttribute("class", "anchorShare anchorPosterContact");
    anchor.href = "javascript:void(0)";
    anchor.innerText = text.modify;
    shareElement.appendChild(anchor);
    shareElement.setAttribute("data-contact-attached", "true");

    anchor.addEventListener("click", () => {
        posterOpenContactModal(shareElement);
    });
}

function posterOpenContactModal(shareElement) {
    const modal = posterGetContactModal();

    modal.shareElement = shareElement;
    modal.querySelector(".posterContactStatus").innerText = "";
    modal.querySelector(".posterContactForm").reset();
    modal.querySelector(".posterContactReason").value = "owner_modify";
    modal.querySelector(".posterContactMessageLimit").innerText =
            "0/" + POSTER_CONTACT_MESSAGE_MAX_LENGTH;
    modal.style.display = "flex";
    modal.querySelector(".posterContactEmail").focus();
}

function posterGetContactModal() {
    let modal = document.getElementById("posterContactModal");
    if (modal) {
        return modal;
    }
    const text = i18nText();

    modal = document.createElement("div");
    modal.id = "posterContactModal";
    modal.className = "posterContactModal";
    modal.innerHTML =
            "<div class='posterContactModalContent'>" +
            "<button class='posterContactClose' type='button' aria-label='" + text.close + "'>×</button>" +
            "<div class='posterContactTitle'>" + text.contactTitle + "</div>" +
            "<form class='posterContactForm' novalidate>" +
            "<label>" + text.contactQuestion + "<select class='posterContactReason' required>" +
            "<option value='owner_modify'>" + text.reasonOwnerModify + "</option>" +
            "<option value='owner_remove'>" + text.reasonOwnerRemove + "</option>" +
            "<option value='other'>" + text.reasonOther + "</option>" +
            "</select></label>" +
            "<label>" + text.yourEmail + "<input class='posterContactEmail' type='email' required maxlength='160'></label>" +
            "<label>" + text.message + "<textarea class='posterContactMessage' required maxlength='" +
            POSTER_CONTACT_MESSAGE_MAX_LENGTH +
            "' rows='5'></textarea></label>" +
            "<input class='posterContactWebsite' type='text' tabindex='-1' autocomplete='off'>" +
            "<div class='posterContactMessageLimit'>0/" +
            POSTER_CONTACT_MESSAGE_MAX_LENGTH +
            "</div>" +
            "<div class='posterContactStatus' aria-live='polite'></div>" +
            "<div class='posterContactActions'>" +
            "<button class='posterContactSubmit' type='submit'>" + text.send + "</button>" +
            "<button class='posterContactCancel' type='button'>" + text.cancel + "</button>" +
            "</div>" +
            "</form>" +
            "</div>";

    document.body.appendChild(modal);

    modal.querySelector(".posterContactClose").addEventListener("click", () => {
        posterCloseContactModal(modal);
    });
    modal.querySelector(".posterContactCancel").addEventListener("click", () => {
        posterCloseContactModal(modal);
    });
    modal.addEventListener("click", (event) => {
        if (event.target === modal) {
            posterCloseContactModal(modal);
        }
    });
    modal.querySelector(".posterContactMessage").addEventListener("input", (event) => {
        modal.querySelector(".posterContactMessageLimit").innerText =
                event.target.value.length + "/" + POSTER_CONTACT_MESSAGE_MAX_LENGTH;
    });
    modal.querySelector(".posterContactForm").addEventListener("submit", (event) => {
        event.preventDefault();
        posterSubmitContactModal(modal);
    });

    return modal;
}

function posterCloseContactModal(modal) {
    modal.style.display = "none";
}

function posterSubmitContactModal(modal) {
    const text = i18nText();
    const form = modal.querySelector(".posterContactForm");
    const status = modal.querySelector(".posterContactStatus");
    const submit = modal.querySelector(".posterContactSubmit");
    const shareElement = modal.shareElement;

    const reason = modal.querySelector(".posterContactReason").value;
    const contactEmail = modal.querySelector(".posterContactEmail").value.trim();
    const message = modal.querySelector(".posterContactMessage").value.trim();
    const website = modal.querySelector(".posterContactWebsite").value.trim();

    status.innerText = "";
    status.classList.remove("posterContactStatusError");

    if (website !== "") {
        posterCloseContactModal(modal);
        return;
    }
    if (!form.checkValidity() || !posterIsValidEmail(contactEmail) || message === "") {
        posterSetContactStatus(status, text.reviewRequired, true);
        return;
    }
    if (!shareElement.dataset.posterId || !shareElement.dataset.publisherId ||
            !shareElement.dataset.href) {
        posterSetContactStatus(status, text.missingPoster, true);
        return;
    }
    if (!POSTER_CONTACT_REASONS[reason]) {
        posterSetContactStatus(status, text.invalidReason, true);
        return;
    }
    if (message.length > POSTER_CONTACT_MESSAGE_MAX_LENGTH) {
        posterSetContactStatus(status, text.messageTooLong, true);
        return;
    }

    const resendKey = posterContactResendKey(contactEmail, shareElement);
    if (posterContactWasRecentlySent(resendKey)) {
        posterSetContactStatus(
                status,
                text.recentlySent,
                true
                );
        return;
    }
    if (_pendingPosterContacts.has(resendKey)) {
        return;
    }

    _pendingPosterContacts.add(resendKey);
    submit.disabled = true;

    fetch(`${CONFIG.subscription_url}${POSTER_CONTACT_ENDPOINT}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded"
        },
        credentials: "include",
        body: new URLSearchParams({
            reason: reason,
            reasonText: text[POSTER_CONTACT_REASONS[reason]],
            posterId: shareElement.dataset.posterId || "",
            publisherId: shareElement.dataset.publisherId || "",
            posterTitle: shareElement.dataset.posterTitle || "",
            posterUrl: shareElement.dataset.href || "",
            contactEmail: contactEmail,
            message: message,
            website: website
        })
    })
            .then(response => {
                if (!response.ok) {
                    throw new Error("HTTP " + response.status);
                }
                posterMarkContactSent(resendKey);
                posterSetContactStatus(status, text.contactReceived);
                setTimeout(() => posterCloseContactModal(modal), 1200);
            })
            .catch(error => {
                console.log("Poster contact error: " + error);
                posterSetContactStatus(
                        status,
                        text.contactProblem,
                        true
                        );
            })
            .finally(() => {
                _pendingPosterContacts.delete(resendKey);
                submit.disabled = false;
            });
}

function posterSetContactStatus(status, message, isError = false) {
    status.innerText = message;
    status.classList.toggle("posterContactStatusError", isError);
}

function posterIsValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

function posterContactResendKey(email, shareElement) {
    return POSTER_CONTACT_SENT_PREFIX +
            email.toLowerCase() +
            ":" +
            (shareElement.dataset.posterId || "") +
            ":" +
            (shareElement.dataset.publisherId || "");
}

function posterContactWasRecentlySent(key) {
    try {
        const sentAt = parseInt(localStorage.getItem(key), 10);
        return !Number.isNaN(sentAt) &&
                Date.now() - sentAt < POSTER_CONTACT_RESEND_DELAY_MS;
    } catch (e) {
        return false;
    }
}

function posterMarkContactSent(key) {
    try {
        localStorage.setItem(key, String(Date.now()));
    } catch (e) {
        // ignore storage errors
    }
}

function posterCreateAndAppend(poster, parent) {
    // appending the poster --------------------------------------------
    const posterElement = posterCreatePosterElement(poster);
    postersApplyPosterImageAspectRatio(posterElement);
    parent.appendChild(posterElement);
    posterApplyResponsiveImageSizing(posterElement);

    // appending author card -------------------------------------------
    posterAppendAuthor(posterElement);

    // facebook share button ----------------------------------------------
    // fbParse(posterElement);

    // poster share button
    posterParse(posterElement);

    return posterElement;
}

labelsFetchCatalog();

function posterApplyProfileSuperLabels(posterElement, author) {
    if (!author || typeof author.space !== "string" || author.space.trim() === "") {
        return;
    }

    const superLabels = new Set(Array.isArray(author.superLabel) ? author.superLabel : []);
    const language = posterElement.getAttribute("lang") === "es" ? "es" : "ca";
    const prefix = language === "es" ? "/es/espacio/" : "/ca/espai/";
    const spaceSlug = encodeURIComponent(postersEscapeSpacesAndApostrophes(author.space));
    const spaceHref = prefix + spaceSlug;
    let hasSpaceLabel = false;
    const labelElements = posterElement.querySelectorAll(
            ".posterLabelWrapper .posterLabel[data-label-value]"
            );
    labelElements.forEach((labelElement) => {
        const label = labelElement.dataset.labelValue || "";
        if (label.trim().toLocaleLowerCase() === author.space.trim().toLocaleLowerCase()) {
            labelElement.href = spaceHref;
            labelElement.classList.add("posterSpaceLabel", "posterSuperLabel");
            hasSpaceLabel = true;
            return;
        }
        if (!superLabels.has(label)) {
            return;
        }

        const labelSlug = encodeURIComponent(postersEscapeSpacesAndApostrophes(label));
        labelElement.href = prefix + spaceSlug + "/" + labelSlug;
        labelElement.classList.add("posterSuperLabel");
    });

    if (!hasSpaceLabel) {
        const wrapper = posterElement.querySelector(".posterLabelWrapper");
        if (wrapper) {
            const spaceLabel = document.createElement("a");
            spaceLabel.className = "posterLabel posterSpaceLabel posterSuperLabel";
            spaceLabel.href = spaceHref;
            spaceLabel.dataset.labelValue = author.space;
            spaceLabel.dataset.labelLanguage = language;
            spaceLabel.textContent = author.space;
            wrapper.appendChild(spaceLabel);
        }
    }
}

function posterAppendAuthor(posterElement) {
    const authorWrappers = posterElement.getElementsByClassName(
            "posterAuthorWrapper"
            );
    (function () {
        const auxElement = authorWrappers[0];
        authorsfetchCard(auxElement.getAttribute("data-author-id")).then(
                (author) => {
            auxElement.innerHTML = authorsGetCard(author);
            auxElement.setAttribute("data-author-attached", true);
            posterApplyProfileSuperLabels(posterElement, author);
        }
        );
    })();
}

export {
posterFetchPosterData,
posterAppendAuthor,
        posterCreateAndAppend,
        posterRender,
        posterStartUp,
        posterParse,
        posterParseDocument,
        posterFetchPosterId,
        POSTER_MINIMUM_WIDTH
};
